import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { useTourStore, tourDoneKey } from "../store/tourStore";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { useT } from "../i18n";

/** Paso del tour: si tiene `selector`, se resalta ese elemento (spotlight);
 *  si tiene `to`, se navega a esa ruta al entrar en el paso. */
interface TourStep {
  title: string;
  body: string;
  selector?: string;
  to?: string;
  plans?: string[]; // solo se muestra si el plan del usuario está en la lista
}

const STEPS: TourStep[] = [
  {
    title: "tour.welcomeTitle",
    body: "tour.welcomeBody",
  },
  {
    title: "tour.dashboardTitle",
    body: "tour.dashboardBody",
    selector: '[data-tour="/dashboard"]', to: "/dashboard",
  },
  {
    title: "tour.changesTitle",
    body: "tour.changesBody",
    selector: '[data-tour="/changes"]', to: "/changes",
  },
  {
    title: "tour.historyTitle",
    body: "tour.historyBody",
    selector: '[data-tour="/history"]', to: "/history",
  },
  {
    title: "tour.radarTitle",
    body: "tour.radarBody",
    selector: '[data-tour="/radar"]', to: "/radar",
  },
  {
    title: "tour.productsTitle",
    body: "tour.productsBody",
    selector: '[data-tour="/products"]', to: "/products",
  },
  {
    title: "tour.competitorsTitle",
    body: "tour.competitorsBody",
    selector: '[data-tour="/competitors"]', to: "/competitors",
  },
  {
    title: "tour.alertsTitle",
    body: "tour.alertsBody",
    selector: '[data-tour="/alerts"]', to: "/alerts", plans: ["pro", "ultra"],
  },
  {
    title: "tour.trendsTitle",
    body: "tour.trendsBody",
    selector: '[data-tour="/trends"]', to: "/trends", plans: ["ultra"],
  },
  {
    title: "tour.pricesTitle",
    body: "tour.pricesBody",
    selector: '[data-tour="/prices"]', to: "/prices", plans: ["ultra"],
  },
  {
    title: "tour.sourcesTitle",
    body: "tour.sourcesBody",
    selector: '[data-tour="/sources"]', to: "/sources",
  },
  {
    title: "tour.doneTitle",
    body: "tour.doneBody",
  },
];

export default function OnboardingTour() {
  const t = useT();
  const { active, step, setStep, stop, start } = useTourStore();
  const user = useAuthStore(s => s.user);
  const impersonating = useAuthStore(s => s.impersonating);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useBreakpoint();
  const [rect, setRect] = useState<DOMRect | null>(null);

  const steps = useMemo(
    () => STEPS.filter(s => !s.plans || (user?.plan && s.plans.includes(user.plan))),
    [user?.plan]
  );
  const current = steps[step];

  // Auto-arranque en el primer login (usuarios de organización, no superadmin)
  useEffect(() => {
    if (!user || active) return;
    if (user.role === "superadmin" && !impersonating) return;
    if (pathname === "/onboarding") return; // primero el asistente, luego el tour
    if (localStorage.getItem(tourDoneKey(user.email))) return;
    const t = setTimeout(start, 900);
    return () => clearTimeout(t);
  }, [user?.email, pathname === "/onboarding"]);

  // Navegar a la ruta del paso y medir el elemento a resaltar
  useEffect(() => {
    if (!active || !current) return;
    if (current.to && pathname !== current.to) navigate(current.to);
    const measure = () => {
      if (!current.selector || isMobile) { setRect(null); return; }
      const el = document.querySelector(current.selector);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const t = setTimeout(measure, 120); // espera al render de la página
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
  }, [active, step, isMobile]);

  if (!active || !current || !user) return null;

  const finish = () => {
    localStorage.setItem(tourDoneKey(user.email), new Date().toISOString());
    stop();
  };

  const isLast = step === steps.length - 1;
  const G = user.primary_color || "#0891b2";

  // Posición de la tarjeta: junto al elemento resaltado o centrada
  const cardStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        left: Math.min(rect.right + 16, window.innerWidth - 396),
        top: Math.max(12, Math.min(rect.top - 12, window.innerHeight - 280)),
      }
    : {
        position: "fixed",
        left: "50%", top: "50%", transform: "translate(-50%, -50%)",
      };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 2000 }}>
      {/* Spotlight: el hueco se hace con un box-shadow gigante */}
      {rect ? (
        <div style={{
          position: "fixed",
          left: rect.left - 6, top: rect.top - 4,
          width: rect.width + 12, height: rect.height + 8,
          borderRadius: 12,
          boxShadow: "0 0 0 9999px rgba(6, 24, 30, 0.62)",
          border: `2px solid ${G}`,
          pointerEvents: "none",
          transition: "all 0.25s ease",
        }} />
      ) : (
        <div style={{ position: "fixed", inset: 0, background: "rgba(6, 24, 30, 0.62)" }} />
      )}

      {/* Tarjeta del paso */}
      <div style={{
        ...cardStyle,
        width: "min(380px, calc(100vw - 24px))",
        background: "var(--clr-modal-solid, #fff)",
        border: "1px solid var(--clr-border)",
        borderRadius: 22,
        boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)",
        padding: "1.25rem 1.4rem 1.1rem",
        color: "var(--clr-text)",
      }}>
        <div style={{ fontSize: "0.7rem", fontWeight: 700, color: G, letterSpacing: "1px", marginBottom: 6 }}>
          {t("tour.step", { n: step + 1, total: steps.length })}
        </div>
        <h3 style={{ margin: "0 0 8px", fontSize: "1.05rem" }}>{t(current.title)}</h3>
        <p style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.55, color: "var(--clr-text2)" }}>
          {t(current.body)}
        </p>

        {/* Progreso */}
        <div style={{ display: "flex", gap: 4, margin: "14px 0" }}>
          {steps.map((_, i) => (
            <div key={i} style={{
              flex: 1, height: 3, borderRadius: 2,
              background: i <= step ? G : "var(--clr-border)",
              transition: "background 0.2s",
            }} />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button onClick={finish} style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: "var(--clr-text4)", fontSize: "0.78rem", padding: "10px 4px",
          }}>
            {t("tour.skip")}
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} style={{
                background: "transparent", border: "1px solid var(--clr-border)",
                borderRadius: 999, cursor: "pointer", color: "var(--clr-text2)",
                fontSize: "0.82rem", padding: "9px 14px", minHeight: 40,
              }}>
                {t("tour.previous")}
              </button>
            )}
            <button onClick={() => (isLast ? finish() : setStep(step + 1))} style={{
              background: G, border: "none", borderRadius: 999, cursor: "pointer",
              color: "#fff", fontSize: "0.82rem", fontWeight: 600,
              padding: "9px 18px", minHeight: 40,
            }}>
              {isLast ? t("tour.finish") : t("tour.next")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
